import React, { useState, useEffect } from 'react';
import { 
  Lock, 
  Search, 
  Plus, 
  ShieldCheck, 
  Server, 
  Clock, 
  Sparkles, 
  SlidersHorizontal,
  ExternalLink,
  Layers,
  Activity,
  Globe
} from 'lucide-react';
import { AppItem, APPS as DEFAULT_APPS, CATEGORIES } from '../data/apps';
import { AppCard } from './AppCard';
import { AddAppModal } from './AddAppModal';

interface DashboardProps {
  onLock: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ onLock }) => {
  const [apps, setApps] = useState<AppItem[]>(DEFAULT_APPS);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState<string>('');
  const [currentDate, setCurrentDate] = useState<string>('');

  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      setCurrentTime(
        now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      );
      setCurrentDate(
        now.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
      );
    };

    updateDateTime();
    const interval = setInterval(updateDateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleAddApp = (newApp: AppItem) => {
    setApps((prev) => [newApp, ...prev]);
  };

  const filteredApps = apps.filter((app) => {
    const matchesCategory =
      selectedCategory === 'All' || app.category === selectedCategory;

    const query = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !query ||
      app.title.toLowerCase().includes(query) ||
      app.subdomain.toLowerCase().includes(query) ||
      app.description.toLowerCase().includes(query) ||
      app.tags.some((tag) => tag.toLowerCase().includes(query));

    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-[#080c14] ambient-mesh text-slate-100 flex flex-col">
      {/* Top Navbar */}
      <header className="sticky top-0 z-30 border-b border-slate-800/80 bg-slate-950/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-tiger-600 via-tiger-500 to-amber-400 p-[1px] shadow-md shadow-tiger-500/20 flex items-center justify-center">
              <div className="w-full h-full bg-slate-950/90 rounded-[11px] flex items-center justify-center">
                <span className="text-tiger-400 font-black text-lg tracking-tighter">🐅</span>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-white tracking-tight text-base sm:text-lg">
                  TIGER HOME
                </span>
                <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-tiger-500/10 text-tiger-400 border border-tiger-500/20">
                  tiger-home.com
                </span>
              </div>
              <p className="text-[11px] text-slate-400 hidden sm:block">Personal Cloud Hub & Gateway</p>
            </div>
          </div>

          {/* Center Info / Live Time (Desktop) */}
          <div className="hidden md:flex items-center gap-4 text-xs font-mono text-slate-400">
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900/80 border border-slate-800">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-slate-300 font-medium">All Systems Live</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-400">
              <Clock className="w-3.5 h-3.5 text-slate-500" />
              <span>{currentDate} • {currentTime}</span>
            </div>
          </div>

          {/* Right Action Buttons */}
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-tiger-500/10 hover:bg-tiger-500/20 text-tiger-400 border border-tiger-500/30 text-xs font-semibold transition-all hover:scale-[1.02]"
              title="Register new subdomain"
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Add App</span>
            </button>

            <button
              onClick={onLock}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-900/80 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-700/80 text-xs font-medium transition-colors"
              title="Lock gateway and sign out"
            >
              <Lock className="w-3.5 h-3.5 text-slate-400" />
              <span>Lock</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        {/* Hero Section */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-slate-800/80">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-tiger-500/10 border border-tiger-500/20 text-xs font-medium text-tiger-400 mb-2">
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Private Subdomain Gateway</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Application Portal
              </h1>
              <p className="text-slate-400 text-sm mt-1 max-w-2xl">
                Direct access to all specialized web apps and home lab services hosted across <span className="text-slate-200 font-mono">tiger-home.com</span>.
              </p>
            </div>

            {/* Quick Metrics */}
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 rounded-xl bg-slate-900/60 border border-slate-800/80 text-center">
                <div className="text-xl font-bold text-white font-mono">{apps.length}</div>
                <div className="text-[11px] text-slate-400 uppercase font-semibold">Active Apps</div>
              </div>
              <div className="px-4 py-2 rounded-xl bg-slate-900/60 border border-slate-800/80 text-center">
                <div className="text-xl font-bold text-emerald-400 font-mono">100%</div>
                <div className="text-[11px] text-slate-400 uppercase font-semibold">Uptime Status</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter and Search Bar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-6">
          {/* Categories */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 scrollbar-none">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-xl text-xs font-medium whitespace-nowrap transition-all ${
                  selectedCategory === cat
                    ? 'bg-tiger-500 text-white font-semibold shadow-md shadow-tiger-500/25'
                    : 'bg-slate-900/70 hover:bg-slate-800/80 text-slate-400 hover:text-slate-200 border border-slate-800/80'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Search apps, tags, subdomains..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-900/80 border border-slate-800 rounded-xl pl-9 pr-4 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-tiger-500 focus:ring-1 focus:ring-tiger-500/20"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 hover:text-slate-300"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Apps Grid */}
        {filteredApps.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredApps.map((app) => (
              <AppCard key={app.id} app={app} />
            ))}

            {/* "Add New App" Slot Card */}
            <div
              onClick={() => setIsAddModalOpen(true)}
              className="group cursor-pointer rounded-2xl border border-dashed border-slate-800 hover:border-tiger-500/50 bg-slate-900/20 hover:bg-slate-900/40 p-6 flex flex-col items-center justify-center text-center transition-all duration-300 hover:-translate-y-1 min-h-[260px]"
            >
              <div className="w-12 h-12 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-500 group-hover:text-tiger-400 group-hover:border-tiger-500/40 group-hover:scale-110 transition-all duration-200 mb-3">
                <Plus className="w-6 h-6" />
              </div>
              <h4 className="text-sm font-bold text-slate-300 group-hover:text-white transition-colors">
                Connect New App
              </h4>
              <p className="text-xs text-slate-400 mt-1 max-w-[220px]">
                Add an upcoming subdomain service to your launcher grid.
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-[11px] font-semibold text-tiger-400 group-hover:underline">
                <span>Configure Subdomain</span>
                <span>→</span>
              </span>
            </div>
          </div>
        ) : (
          <div className="py-16 text-center rounded-2xl border border-slate-800 bg-slate-900/40 p-8">
            <SlidersHorizontal className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-white">No applications match your search</h3>
            <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
              We couldn't find any app matching "{searchQuery}" in category "{selectedCategory}".
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('All');
              }}
              className="mt-4 px-4 py-2 rounded-xl bg-slate-800 text-xs text-slate-300 hover:text-white transition-colors"
            >
              Reset Filters
            </button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800/80 bg-slate-950/60 py-6 text-xs text-slate-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-tiger-500 font-bold">tiger-home.com</span>
            <span>•</span>
            <span>Protected Cloud Subdomain Hub</span>
          </div>

          <div className="flex items-center gap-4 text-[11px] text-slate-400">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              All Subdomains Active
            </span>
            <span>Cloudflare Pages</span>
            <span>Master Access Enabled</span>
          </div>
        </div>
      </footer>

      {/* Add App Modal */}
      <AddAppModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddApp={handleAddApp}
      />
    </div>
  );
};
